import React, { useState } from "react";
import { Link2, Check } from "lucide-react";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
    <path d="M17.5 14.4c-.3-.1-1.6-.8-1.9-.9-.3-.1-.4-.1-.6.1-.2.2-.7.9-.9 1-.2.2-.3.2-.6.1-.9-.4-1.9-1-2.7-2-.4-.5-.1-.7.1-.9.2-.2.4-.5.6-.7.1-.2.2-.4.1-.6-.1-.2-.7-1.6-.9-2.1-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.2-.9.9-.9 2.1 0 1.2.9 2.4 1 2.6.1.1 1.5 2.3 3.7 3.2 2.2.9 2.2.6 2.6.5.4 0 1.3-.5 1.5-1 .2-.5.2-1 .1-1.1-.1-.1-.2-.2-.3-.2z" />
    <path d="M12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.4 5L2 22l5.2-1.4c1.4.8 3.1 1.2 4.8 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3.1.8.8-3-.2-.3c-.9-1.4-1.3-3-1.3-4.6C3.5 7.3 7.3 3.5 12 3.5S20.5 7.3 20.5 12 16.7 20.2 12 20.2z" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
    <path d="M17.5 3h3l-6.6 7.5L21.5 21h-6.1l-4.8-6.3L4.9 21H1.8l7.1-8.1L1 3h6.2l4.4 5.8L17.5 3zm-1.1 16h1.7L7.7 5H5.9l10.5 14z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
    <path d="M22 12a10 10 0 10-11.6 9.9v-7h-2.5v-2.9h2.5V9.8c0-2.5 1.5-3.9 3.7-3.9 1.1 0 2.2.2 2.5.3v2.7h-1.4c-1.4 0-1.7.6-1.7 1.6v2h3l-.4 2.9h-2.6v7A10 10 0 0022 12z" />
  </svg>
);

export const ShareButtons: React.FC<{ url: string; title: string }> = ({ url, title }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setCopied(true);
      } catch {
        setCopied(false);
      }
    }
    setTimeout(() => setCopied(false), 2000);
  };

  const links = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: <WhatsAppIcon />,
      href: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
    },
    {
      key: "twitter",
      label: "X",
      icon: <XIcon />,
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: <FacebookIcon />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
  ];

  return (
    <div data-testid="share-buttons" className="flex flex-wrap items-center gap-3">
      <p className="text-sm font-semibold text-cvmuted">Share this</p>
      <div className="flex flex-wrap gap-2">
        {links.map((l) => (
          <a
            key={l.key}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`share-${l.key}`}
            className="inline-flex items-center gap-2 rounded-[10px] border border-cvborder bg-white px-3 py-1.5 text-xs font-medium text-ink transition-colors duration-200 ease-out hover:bg-periwinkle/20"
          >
            {l.icon}
            {l.label}
          </a>
        ))}
        <button
          type="button"
          onClick={handleCopy}
          data-testid="share-copy-link"
          className="inline-flex items-center gap-2 rounded-[10px] border border-cvborder bg-white px-3 py-1.5 text-xs font-medium text-ink transition-colors duration-200 ease-out hover:bg-periwinkle/20"
        >
          {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
          {copied ? "Copied!" : "Copy link"}
        </button>
      </div>
    </div>
  );
};
